import type { ColumnDef } from '@tanstack/react-table';
import { createColumnHelper } from '@tanstack/react-table';
import type { FunctionComponent } from 'react';
import { useMemo } from 'react';
import Maybe from 'true-myth/maybe';

import { useTranslate } from '~/shared/context/use-translate';
import type { LanguageKey } from '~/shared/internationalization';
import type { Translate, TranslationKey } from '~/shared/internationalization/translation';
import { Badge } from '~/ui/core/badge/Badge';
import { Checkbox } from '~/ui/core/checkbox/Checkbox';
import { IconDelete, IconFileDownload } from '~/ui/core/icons';
import { Spinner } from '~/ui/core/spinner/Spinner';
import { TableActionButton } from '~/ui/core/table/Table';
import { Tooltip } from '~/ui/core/tooltip/Tooltip';

import { formatDateAndTimeByLocale } from '../../../../date/date-formatter';
import type { DocumentStatus, UploadedDocument } from './types';
import { isDocumentStatus } from './types';

const columnHelper = createColumnHelper<UploadedDocument>();

const STATUS_TRANSLATION_KEYS: Record<DocumentStatus, TranslationKey> = {
    DELETED: 'pages.job-architecture-v2.knowledge-base.status.DELETED',
    ERROR: 'pages.job-architecture-v2.knowledge-base.status.ERROR',
    INDEXED: 'pages.job-architecture-v2.knowledge-base.status.INDEXED',
    PROCESSING: 'pages.job-architecture-v2.knowledge-base.status.PROCESSING',
    UPLOAD_FAILED: 'pages.job-architecture-v2.knowledge-base.status.UPLOAD_FAILED',
    UPLOADING: 'pages.job-architecture-v2.knowledge-base.status.UPLOADING',
};

const getStatusVariant = (status: string): 'error' | 'success' | 'warning' => {
    switch (status) {
        case 'ERROR':
            return 'error';
        case 'INDEXED':
        case 'PROCESSING':
            return 'success';
        default:
            return 'warning';
    }
};

const StatusBadge: FunctionComponent<{ readonly status: string }> = ({ status }) => {
    const translate = useTranslate();
    const displayStatus: DocumentStatus = isDocumentStatus(status) ? status : 'ERROR';

    if (status === 'UPLOADING') {
        return <Spinner />;
    }

    return <Badge variant={getStatusVariant(status)}>{translate(STATUS_TRANSLATION_KEYS[displayStatus])}</Badge>;
};

const ActionsCell: FunctionComponent<{
    readonly id: string;
    readonly name: string;
    readonly onDelete: (id: string, name: string) => void;
    readonly onDownload: (id: string) => void;
}> = ({ id, name, onDelete, onDownload }) => {
    const translate = useTranslate();

    return (
        <div className="flex w-full items-center justify-end">
            <Tooltip>
                <Tooltip.Trigger asChild={true}>
                    <TableActionButton
                        aria-label={translate('common.download')}
                        onClick={(e) => {
                            e.stopPropagation();
                            onDownload(id);
                        }}
                    >
                        <IconFileDownload size={16} />
                    </TableActionButton>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                    <Tooltip.Content side="top">{translate('common.download')}</Tooltip.Content>
                </Tooltip.Portal>
            </Tooltip>
            <Tooltip>
                <Tooltip.Trigger asChild={true}>
                    <TableActionButton
                        aria-label={translate('common.delete')}
                        onClick={(e) => {
                            e.stopPropagation();
                            onDelete(id, name);
                        }}
                    >
                        <IconDelete size={16} />
                    </TableActionButton>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                    <Tooltip.Content side="top">{translate('common.delete')}</Tooltip.Content>
                </Tooltip.Portal>
            </Tooltip>
        </div>
    );
};

const createCheckboxColumn = (): ColumnDef<UploadedDocument> =>
    columnHelper.display({
        id: 'checkbox',
        cell: ({ row }) => (
            <Checkbox
                checked={row.getIsSelected()}
                onCheckedChange={(checked) => row.toggleSelected(checked === true)}
            />
        ),
        header: ({ table }) => (
            <Checkbox
                checked={table.getIsAllRowsSelected()}
                onCheckedChange={(checked) => table.toggleAllRowsSelected(checked === true)}
            />
        ),
    });

const createDocumentColumn = (translate: Translate): ColumnDef<UploadedDocument> =>
    columnHelper.accessor('name', {
        id: 'name',
        cell: ({ getValue }) => getValue(),
        header: translate('common.document'),
    });

const createStatusColumn = (translate: Translate): ColumnDef<UploadedDocument> =>
    columnHelper.accessor('status', {
        id: 'status',
        cell: ({ getValue }) => <StatusBadge status={getValue()} />,
        header: translate('common.status'),
    });

const createLastModifiedColumn = (translate: Translate, locale: LanguageKey): ColumnDef<UploadedDocument> =>
    columnHelper.accessor('updatedAt', {
        id: 'updatedAt',
        cell: ({ getValue }) =>
            formatDateAndTimeByLocale({
                dateStringToFormat: Maybe.of(getValue()),
                locale,
            }).unwrapOr(''),
        header: translate('common.last-modified'),
    });

const createActionsColumn = (
    translate: Translate,
    onDelete: (id: string, name: string) => void,
    onDownload: (id: string) => void,
): ColumnDef<UploadedDocument> =>
    columnHelper.display({
        id: 'actions',
        cell: ({ row }) => (
            <ActionsCell id={row.original.id} name={row.original.name} onDelete={onDelete} onDownload={onDownload} />
        ),
        header: translate('common.actions'),
    });

type UseColumnsOptions = {
    readonly locale: LanguageKey;
    readonly onDelete: (id: string, name: string) => void;
    readonly onDownload: (id: string) => void;
    readonly translate: Translate;
};

export const useColumns = ({ locale, onDelete, onDownload, translate }: UseColumnsOptions) =>
    // biome-ignore lint/correctness/useExhaustiveDependencies: callbacks are intentionally excluded; TanStack Table requires stable column references to propagate updates through its internal memo chain.
    useMemo(
        () => [
            createCheckboxColumn(),
            createDocumentColumn(translate),
            createStatusColumn(translate),
            createLastModifiedColumn(translate, locale),
            createActionsColumn(translate, onDelete, onDownload),
        ],
        [locale, translate],
    );
